import { useMemo } from 'react';
import { MenuItem } from '@mui/material';

import { appendInputIdToLabel } from 'utils';
import { defaultLanguage, useTranslationContext } from 'context';

import { InputField, AutocompleteField } from '../fields';

import { FormSelectFieldProps } from './types';

export const FormSelectField = ({
  id,
  label,
  inputId,
  options,
  placeholder,
  isSearchable,
  translations,
  labelOutside = true,
}: FormSelectFieldProps) => {
  const { language } = useTranslationContext();

  const translation = useMemo(() => {
    if (!language || !translations || language === defaultLanguage) {
      return {
        placeholder,
        label: appendInputIdToLabel(label, inputId),
        options: options?.options
          ? options?.options.map((value) => ({ value, label: value }))
          : [],
      };
    }

    const translation = translations[language];
    const langOptions = translation.options || options?.options;
    return {
      placeholder: translation?.placeholder || placeholder,
      label: appendInputIdToLabel(
        label ? translation?.label || label : label,
        inputId
      ),
      options: langOptions
        ? langOptions.map((value, index) => ({
            label: value,
            value: options?.options?.[index] || value,
          }))
        : [],
    };
  }, [label, placeholder, options?.options, language, translations, inputId]);

  return isSearchable && !options?.multiple ? (
    <AutocompleteField
      name={id}
      label={translation.label}
      options={translation.options}
      placeholder={translation.placeholder}
    />
  ) : (
    <InputField
      select
      name={id}
      fullWidth
      className="selectInput"
      label={translation.label}
      labelOutside={labelOutside}
      placeholder={translation.placeholder}
      slotProps={{
        select: {
          multiple: options?.multiple,
        },
      }}
    >
      {translation.options &&
        translation.options.map(({ value, label }) => (
          <MenuItem key={value} value={value}>
            {label}
          </MenuItem>
        ))}
    </InputField>
  );
};
